import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { adminGuard } from './core/guards/admin.guard';
import { permissionGuard } from './core/guards/permission.guard';
import { homeGuard } from './core/guards/home.guard';
import { telecomGuard } from './core/guards/telecom.guard';
import { billingRedirectGuard } from './core/guards/billing-redirect.guard';

export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
  {
    path: 'welcome',
    loadComponent: () => import('./pages/welcome/welcome.component').then(m => m.WelcomeComponent),
  },
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
  },
  {
    // Break-glass super-admin login. PUBLIC (no auth guard) — exists so
    // engineering can recover the system when SSO is down. Never linked
    // from the regular login UI; admins reach it by typing the URL.
    path: 'superadmin/login',
    loadComponent: () => import('./pages/superadmin-login/superadmin-login.component').then(m => m.SuperadminLoginComponent),
  },
  {
    path: 'auth/microsoft/complete',
    loadComponent: () => import('./pages/auth-microsoft-complete/auth-microsoft-complete.component').then(m => m.AuthMicrosoftCompleteComponent),
  },
  {
    // SP-initiated SSO entry point. Connected portals (PartnerPortal,
    // LicensePortal, 3CX Reporting…) point their "Continue with OneGate"
    // button at /sso?portal=<audience>&return=<callback-url>. The component
    // handles the not-signed-in case by redirecting to /login?next=…
    path: 'sso',
    loadComponent: () => import('./pages/sso-handoff/sso-handoff.component').then(m => m.SsoHandoffComponent),
  },
  {
    // Public landing page for the signup verification email link.
    path: 'verify-email',
    loadComponent: () => import('./pages/verify-email/verify-email.component').then(m => m.VerifyEmailComponent),
  },
  {
    // Public landing page for the password reset / "set your password" email link.
    path: 'reset-password',
    loadComponent: () => import('./pages/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
  },
  // Customer organizations part 2 (Owais, 30 Oct 2026): the links of the organization emails, outside the customer shell. Accept
  // also answers at /my-account/organization/accept: after sign-in the website returns only to My Account paths.
  { path:'organization/confirm',loadComponent:()=>import('./pages/organization-token/organization-confirm.component').then(m=>m.OrganizationConfirmComponent) },
  { path:'organization/complete',loadComponent:()=>import('./pages/organization-token/organization-complete.component').then(m=>m.OrganizationCompleteComponent) },
  { path:'organization/accept',loadComponent:()=>import('./pages/organization-token/organization-accept.component').then(m=>m.OrganizationAcceptComponent) },
  { path:'my-account/organization/accept',loadComponent:()=>import('./pages/organization-token/organization-accept.component').then(m=>m.OrganizationAcceptComponent) },
  {
    path: '',
    loadComponent: () => import('./layout/customer-shell/customer-shell.component').then(m => m.CustomerShellComponent),
    canActivate: [authGuard],
    children: [
      { path:'my-account/profile',canActivate:[permissionGuard],data:{permissions:['account.read','account.profile.read','account.billing.read','account.billing.write']},loadComponent:()=>import('./pages/customer-profile/customer-profile.component').then(m=>m.CustomerProfileComponent) },
      // Customer organizations (Owais, 30 Oct 2026): My Account → Users. The page asks GET /account/organization and leaves for My Account on 404.
      { path:'my-account/users',loadComponent:()=>import('./pages/organization-users/organization-users.component').then(m=>m.OrganizationUsersComponent) },
      { path:'my-account/telecom',canActivate:[billingRedirectGuard],loadComponent:()=>import('./pages/telecom/telecom.component').then(m=>m.TelecomComponent) },
      {
        path: 'my-account',
        canActivate: [homeGuard, permissionGuard, billingRedirectGuard],
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        data: {
          permissions: [
            'account.read',
            'account.profile.read',
            'account.billing.read',
            'account.billing.write',
            'account.cart.read',
            'account.cart.write',
            'account.orders.read',
            'account.orders.write',
            'account.invoices.read',
            'account.portals.read',
          ],
        },
        loadComponent: () => import('./pages/my-account/my-account.component').then(m => m.MyAccountComponent),
      },
    ],
  },
  {
    path: '',
    loadComponent: () => import('./layout/shell/shell.component').then(m => m.ShellComponent),
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        canActivate: [homeGuard],
        loadComponent: () => import('./pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
      },
      {
        path: 'customer-accounts',
        canActivate: [permissionGuard],
        data: { permissions: ['account.customers.read'] },
        loadComponent: () => import('./pages/customer-accounts/customer-accounts.component').then(m => m.CustomerAccountsComponent),
      },
      { path:'telecom-billing',canActivate:[telecomGuard],data:{telecomAdmin:true},loadComponent:()=>import('./pages/telecom/telecom.component').then(m=>m.TelecomComponent) },
      {
        path: 'profile',
        loadComponent: () => import('./pages/profile/profile.component').then(m => m.ProfileComponent),
      },
      {
        path: 'feature-requests',
        canActivate: [permissionGuard],
        data: { permissions: ['feedback.read'] },
        loadComponent: () => import('./pages/feature-requests/feature-requests.component').then(m => m.FeatureRequestsComponent),
      },
      {
        path: 'usa-browser',
        canActivate: [permissionGuard],
        data: { permissions: ['tools.usa-browser'] },
        loadComponent: () => import('./pages/usa-browser/usa-browser.component').then(m => m.UsaBrowserComponent),
      },
      {
        path: 'admin',
        canActivate: [adminGuard],
        children: [
          { path: 'iam', loadComponent: () => import('./pages/admin/iam/iam.component').then(m => m.IamComponent) },
          // Backwards-compatible redirects from the old separate routes
          { path: 'users', redirectTo: 'iam', pathMatch: 'full' },
          { path: 'roles', redirectTo: 'iam?tab=roles', pathMatch: 'full' },
          { path: 'portals', loadComponent: () => import('./pages/admin/portals/portals.component').then(m => m.PortalsComponent) },
          { path: 'access-requests', loadComponent: () => import('./pages/admin/access-requests/access-requests.component').then(m => m.AccessRequestsComponent) },
          // Customer organizations (Owais, 30 Oct 2026): staff reassign the primary contact (Change Primary Contact needs customers.organizations.manage).
          { path: 'organizations', canActivate: [permissionGuard], data: { permissions: ['customers.organizations.read'] }, loadComponent: () => import('./pages/admin/organizations/admin-organizations.component').then(m => m.AdminOrganizationsComponent) },
          { path: 'organizations/:id', canActivate: [permissionGuard], data: { permissions: ['customers.organizations.read'] }, loadComponent: () => import('./pages/admin/organizations/admin-organization-detail.component').then(m => m.AdminOrganizationDetailComponent) },
          { path: 'audit', loadComponent: () => import('./pages/admin/audit/audit.component').then(m => m.AuditComponent) },
          { path: 'webhook-events', loadComponent: () => import('./pages/admin/webhook-events/webhook-events.component').then(m => m.WebhookEventsComponent) },
          { path: 'intent', canActivate: [permissionGuard], data: { permissions: ['intent.read'] }, loadComponent: () => import('./pages/admin/intent-engine/intent-engine.component').then(m => m.IntentEngineComponent) },
          { path: 'mappings', canActivate: [permissionGuard], data: { permissions: ['platform.admin'] }, loadComponent: () => import('./pages/admin/mappings/mappings.component').then(m => m.MappingsComponent) },
          { path: 'automations', loadComponent: () => import('./pages/admin/automations/automations.component').then(m => m.AutomationsComponent) },
          { path: 'automations/:id', loadComponent: () => import('./pages/admin/automations/builder/builder.component').then(m => m.AutomationBuilderComponent) },
          { path: 'settings/deal-settings', canActivate: [permissionGuard], data: { permissions: ['intent.read'] }, loadComponent: () => import('./pages/admin/intent-engine/intent-engine.component').then(m => m.IntentEngineComponent) },
          { path: 'settings', loadComponent: () => import('./pages/admin/settings/settings.component').then(m => m.SettingsComponent) },
          { path: 'settings/integrations', loadComponent: () => import('./pages/admin/settings/integrations/integrations.component').then(m => m.IntegrationsComponent) },
          { path: 'settings/payments', loadComponent: () => import('./pages/admin/settings/payments/payments.component').then(m => m.PaymentsComponent) },
          { path: 'settings/tax-setup', loadComponent: () => import('./pages/admin/settings/tax-setup/tax-setup.component').then(m => m.TaxSetupComponent) },
          { path: 'settings/product-catalog', canActivate: [permissionGuard], data: { permissions: ['settings.read'] }, loadComponent: () => import('./pages/admin/settings/product-catalog/product-catalog.component').then(m => m.ProductCatalogComponent) },
          { path: 'settings/rbac', loadComponent: () => import('./pages/admin/settings/rbac/rbac.component').then(m => m.RbacComponent) },
          { path: '', redirectTo: 'iam', pathMatch: 'full' },
        ],
      },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ],
  },
  { path: '**', redirectTo: 'welcome' },
];
